import { useTranslator } from "@eo-locale/react";

import { FormEvent, useEffect, useRef } from "react";

import { Button, useToast, VStack } from "@/storybook-components";

import EmailQuestion, {
  validate as validateEmail
} from "@/components/questions/email-question";
import TosQuestion, {
  validate as validateTos
} from "@/components/questions/tos-question";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import { createLoginOtp } from "@/redux/features/user/thunk";
import {
  clearAllErrors,
  resetAnswers,
  setError,
  setStatus
} from "@/redux/features/questions/slice";
import { highlightError } from "@/helpers/highlight-error";
import InvisibleReCaptcha from "@/components/recaptcha/invisible";
import { setClientRecaptchaValidation } from "@/redux/features/recaptcha/slice";
import Alerts from "@/components/alerts";
import { resetGuestLogin } from "@/redux/features/user/slice";

interface EmailLoginFormProps {
  onBack: () => void;
  onNext: () => void;
}

const EmailLoginForm = ({ onBack, onNext }: EmailLoginFormProps) => {
  const toast = useToast();
  const recaptchaRef = useRef(null);
  const dispatch = useAppDispatch();
  const { translate } = useTranslator();
  const { answers, status } = useAppSelector((state) => state.questions);
  const { status: guestLoginStatus, error } = useAppSelector(
    (state) => state.user.guestLogin
  );
  const { recaptchaSignupEnabled } = useAppSelector(
    (state) => state.settings.settings
  );

  const isReCaptchaEnabled =
    recaptchaSignupEnabled && process.env.RECAPTCHA_INVISIBLE_SITE_KEY;

  useEffect(() => {
    toast.closeAll();

    if (guestLoginStatus === "success") {
      onNext();
    } else if (guestLoginStatus === "error") {
      toast({
        title: translate("tools.guestLoginFailed"),
        status: "error",
        duration: 5000,
        isClosable: true
      });
    }
  }, [guestLoginStatus, onNext, toast, translate]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    const emailError = await validateEmail(answers["email"] || "", {
      id: "email",
      required: true
    });
    if (emailError?.length) {
      dispatch(setError({ questionId: "email", error: emailError }));
    }

    // Validate terms of service
    const tosError = await validateTos(answers["tosQuestion"] || "");
    if (tosError?.length) {
      dispatch(setError({ questionId: "tosQuestion", error: tosError }));
    }

    if (emailError?.length || tosError?.length) {
      dispatch(setStatus("idle"));
      highlightError();
      return;
    }

    dispatch(setStatus("saving"));

    if (isReCaptchaEnabled) {
      const recaptchaToken = await recaptchaRef.current.executeAsync();
      dispatch(setClientRecaptchaValidation(recaptchaToken));
      recaptchaRef.current.reset();
    }

    await dispatch(createLoginOtp());
    dispatch(setStatus("idle"));
  };

  const handleReset = () => {
    dispatch(resetAnswers());
    dispatch(clearAllErrors());

    dispatch(resetGuestLogin());
    onBack();
  };

  return (
    <VStack
      as="form"
      noValidate
      gap={4}
      align="stretch"
      onSubmit={handleSubmit}
    >
      <div>
        <EmailQuestion
          isAsterixRequired={false}
          question={{
            id: "email",
            required: true,
            question: translate("common.email")
          }}
        />
        <TosQuestion
          isAsterixRequired={false}
          question={{
            id: "tosQuestion",
            required: true,
            question: translate("common.termsAndConditions", {
              target: "_blank"
            })
          }}
        />
      </div>
      {isReCaptchaEnabled && <InvisibleReCaptcha recaptchaRef={recaptchaRef} />}
      {error && (
        <Alerts
          status="error"
          title={translate("errors.sorrySomethingWentWrong")}
        />
      )}
      <VStack gap={3} width="100%">
        <Button
          width="100%"
          variant="primary"
          type="submit"
          loading={status === "saving"}
        >
          {translate("profileGate.sendCode")}
        </Button>
        <Button
          width="100%"
          variant="secondary"
          type="reset"
          onClick={handleReset}
          disabled={status === "saving"}
        >
          {translate("common.sentenceCasedBack")}
        </Button>
      </VStack>
    </VStack>
  );
};

export default EmailLoginForm;
